import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ErrorFallbackScreen } from 'components/screens';
import { PATHS } from 'config/paths';
import { GuestCvErrorBoundary } from 'modules/guest-cv/GuestCvErrorBoundary';
import { GuestFormErrorBoundary } from 'modules/guest-forms/GuestFormErrorBoundary';
import {
  AccountPage,
  AppLandingPage,
  AssignmentDetailsPage,
  AssignmentsPage,
  AuthenticationErrorPage,
  ContactZenjobPage,
  CVBuilderPage,
  DeleteProfilePage,
  GeolocationPermissionsPage,
  GuestCvBuilderPage,
  GuestFormPage,
  LanguageSettingsPage,
  LoginPage,
  NotificationSettingsPage,
  PrivacySettingsPage,
  ReasonDetailsPage,
  ShiftDetailsPage,
  WeekDetailsPage,
  WeeksPage,
  WorkPage
} from 'modules/routes';
import { AuthGuard } from './utils/auth';
import { Auth0ProviderWithNavigate } from './utils/auth/components/Auth0ProviderWithNavigate';

// eslint-disable-next-line react-refresh/only-export-components
const RedirectToWeeks = () => <Navigate to={PATHS.weeks} replace />;

export const router = createBrowserRouter([
  {
    children: [
      {
        path: PATHS.app,
        element: <AppLandingPage />
      },
      {
        path: PATHS.error,
        element: <AuthenticationErrorPage />
      },
      {
        // Public magic-link CV builder — intentionally OUTSIDE Auth0ProviderWithNavigate
        // so external applicants are never redirected to login. Wrapped in an error boundary
        // because this route has no react-router errorElement (only the auth subtree does),
        // so an uncaught render error would otherwise white-screen the applicant unreported.
        path: PATHS.guestCv,
        element: (
          <GuestCvErrorBoundary>
            <GuestCvBuilderPage />
          </GuestCvErrorBoundary>
        )
      },
      {
        // Public magic-link form runner — same placement and reasoning as the guest CV route
        // above: outside Auth0 so an invited applicant is never redirected to a login they
        // cannot pass, and inside its own error boundary because this route has no
        // react-router errorElement.
        path: PATHS.guestForm,
        element: (
          <GuestFormErrorBoundary>
            <GuestFormPage />
          </GuestFormErrorBoundary>
        )
      },
      {
        element: <Auth0ProviderWithNavigate />,
        errorElement: <ErrorFallbackScreen />,
        children: [
          {
            path: PATHS.root,
            element: <AuthGuard component={RedirectToWeeks} />
          },
          {
            path: PATHS.login,
            element: <LoginPage />
          },
          {
            path: PATHS.cvBuilder,
            element: <AuthGuard component={CVBuilderPage} />
          },
          {
            path: PATHS.weeks,
            element: <AuthGuard component={WorkPage} />,
            children: [
              {
                index: true,
                element: <WeeksPage />
              },
              {
                path: PATHS.weekDetails,
                children: [
                  {
                    index: true,
                    element: <WeekDetailsPage />
                  },
                  {
                    path: PATHS.shiftDetails,
                    children: [
                      {
                        index: true,
                        element: <ShiftDetailsPage />
                      }
                    ]
                  },
                  {
                    path: PATHS.reasonDetails,
                    children: [
                      {
                        index: true,
                        element: <ReasonDetailsPage />
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            path: PATHS.account,
            children: [
              {
                index: true,
                element: <AuthGuard component={AccountPage} />
              },
              {
                path: PATHS.languageSettings,
                element: <AuthGuard component={LanguageSettingsPage} />
              },
              {
                path: PATHS.privacySettings,
                children: [
                  {
                    index: true,
                    element: <AuthGuard component={PrivacySettingsPage} />
                  },
                  {
                    path: PATHS.geolocation,
                    element: (
                      <AuthGuard component={GeolocationPermissionsPage} />
                    )
                  }
                ]
              },
              {
                path: PATHS.contactZenjob,
                element: <AuthGuard component={ContactZenjobPage} />
              },
              {
                path: PATHS.deleteProfile,
                element: <AuthGuard component={DeleteProfilePage} />
              },
              {
                path: PATHS.assignments,
                children: [
                  {
                    index: true,
                    element: <AuthGuard component={AssignmentsPage} />
                  },
                  {
                    path: PATHS.assignmentDetails,
                    element: <AuthGuard component={AssignmentDetailsPage} />
                  }
                ]
              },
              {
                path: PATHS.notificationSettings,
                element: <AuthGuard component={NotificationSettingsPage} />
              }
            ]
          },
          {
            path: '*',
            element: <AuthGuard component={ErrorFallbackScreen} />
          }
        ]
      }
    ]
  }
]);
